import { PDFDocument, PDFViewerState } from './types.js';

// Dynamic import for PDF.js with proper typing
let pdfjsLib: any = null;

// Initialize PDF.js with local files
async function initPDFJS() {
  if (!pdfjsLib) {
    try {
      const [pdfjsModule, pdfjsWorkerModule] = await Promise.all([
        import('pdfjs-dist'),
        // Inline worker avoids runtime asset fetches that can fail behind proxies/CDNs.
        import('pdfjs-dist/build/pdf.worker.mjs?worker&inline')
      ]);
      pdfjsLib = pdfjsModule;
      const PDFWorker = pdfjsWorkerModule.default;
      pdfjsLib.GlobalWorkerOptions.workerPort = new PDFWorker();
    } catch (error) {
      console.error('Failed to load PDF.js:', error);
      throw new Error('PDF.js library could not be loaded');
    }
  }
  return pdfjsLib;
}

export class PDFViewer {
  private pdfDoc: any | null = null;
  private currentPage = 1;
  private totalPages = 1;
  private isLoading = false;
  private error: string | null = null;

  private canvasContainer: HTMLElement;
  private canvases: HTMLCanvasElement[] = [];

  private stateChangeCallbacks: ((state: PDFViewerState) => void)[] = [];
  private intersectionObserver: IntersectionObserver | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private isRestoringInitialPage = false;
  private hasInitialPageState = false;
  private isClosed = false;

  constructor(
    private containerElement: HTMLElement,
    private onStateChange?: (state: PDFViewerState) => void,
    private onClose?: () => void
  ) {
    this.canvasContainer = containerElement.querySelector('.canvas-container') as HTMLElement;

    this.setupEventListeners();
    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    // Set up intersection observer to track which page is currently visible
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        let mostVisible = entries[0];
        let maxRatio = entries[0].intersectionRatio;

        // Find the canvas with the highest intersection ratio
        entries.forEach(entry => {
          if (entry.intersectionRatio > maxRatio) {
            mostVisible = entry;
            maxRatio = entry.intersectionRatio;
          }
        });

        if (this.isRestoringInitialPage) {
          return;
        }

        // Update current page based on most visible canvas
        if (mostVisible && mostVisible.target) {
          const pageNum = parseInt((mostVisible.target as HTMLElement).dataset.pageNum || '1');
          if (pageNum !== this.currentPage && maxRatio > 0.3) {
            this.currentPage = pageNum;
            this.emitStateChange();
          }
        }
      },
      {
        root: this.canvasContainer,
        threshold: Array.from({ length: 101 }, (_, i) => i / 100) // Check every 1%
      }
    );
  }

  private setupEventListeners(): void {
    // Keyboard navigation - only when container is visible/in the DOM
    this.keydownHandler = (e: KeyboardEvent) => {
      if (!this.containerElement || !document.body.contains(this.containerElement)) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          this.previousPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          this.nextPage();
          break;
        case 'Escape':
          e.preventDefault();
          this.close();
          break;
        case 'Home':
          e.preventDefault();
          this.scrollToPage(1);
          break;
        case 'End':
          e.preventDefault();
          this.scrollToPage(this.totalPages);
          break;
      }
    };

    document.addEventListener('keydown', this.keydownHandler);
  }

  async loadPDF(pdf: PDFDocument): Promise<void> {
    try {
      this.setLoading(true);
      this.error = null;

      // Initialize PDF.js
      const pdfjs = await initPDFJS();

      // Load the PDF document
      const loadingTask = pdfjs.getDocument(pdf.url);
      this.pdfDoc = await loadingTask.promise;

      this.totalPages = this.pdfDoc.numPages;
      this.currentPage = 1;
      this.hasInitialPageState = false;
      this.isClosed = false;

      // Clear existing canvases
      this.clearCanvases();

      this.isRestoringInitialPage = true;

      // Create all canvases for all pages
      await this.createAllCanvases();

      this.setLoading(false);
      this.hasInitialPageState = true;
      this.emitStateChange();

      // Ignore stale IntersectionObserver callbacks from initial layout/scroll.
      window.setTimeout(() => {
        this.isRestoringInitialPage = false;
        this.reconcileVisiblePage();
      }, 250);

    } catch (error) {
      this.isRestoringInitialPage = false;
      console.error('Error loading PDF:', error);
      this.error = error instanceof Error ? error.message : 'Failed to load PDF';
      this.setLoading(false);
      this.emitStateChange();
      throw error;
    }
  }

  private clearCanvases(): void {
    // Clear existing canvases
    this.canvases.forEach(canvas => {
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(canvas);
      }
    });
    this.canvases = [];
    this.canvasContainer.innerHTML = '';
  }

  private async createAllCanvases(): Promise<void> {
    if (!this.pdfDoc) return;

    // Create a container for all pages
    const pagesContainer = document.createElement('div');
    pagesContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 15px;
      padding: 10px 0;
      width: 100%;
    `;

    // Create and render all pages
    for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
      const canvas = await this.createCanvasForPage(pageNum);
      this.canvases.push(canvas);
      pagesContainer.appendChild(canvas);
      
      // Observe canvas for intersection
      if (this.intersectionObserver) {
        this.intersectionObserver.observe(canvas);
      }
    }

    this.canvasContainer.appendChild(pagesContainer);
  }

  private async createCanvasForPage(pageNum: number): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    canvas.dataset.pageNum = pageNum.toString();

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    try {
      // Get the page
      const page = await this.pdfDoc.getPage(pageNum);

      // Calculate the container width (95% of canvas container)
      const containerWidth = this.canvasContainer.clientWidth * 0.95;

      // Get page dimensions at scale 1 to calculate aspect ratio
      const baseViewport = page.getViewport({ scale: 1 });
      const aspectRatio = baseViewport.width / baseViewport.height;

      // Calculate scale to fit width (browser zoom will handle further scaling)
      const scale = containerWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });

      // Get device pixel ratio for high-DPI displays (retina, etc.)
      const devicePixelRatio = window.devicePixelRatio || 1;

      // Set canvas dimensions to actual render size (scaled for high-DPI)
      canvas.width = viewport.width * devicePixelRatio;
      canvas.height = viewport.height * devicePixelRatio;

      // Style the canvas to display at the desired size
      canvas.style.cssText = `
        width: ${containerWidth}px;
        height: ${containerWidth / aspectRatio}px;
        background: white;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        margin-bottom: 10px;
      `;

      // Scale context for high-DPI rendering
      ctx.scale(devicePixelRatio, devicePixelRatio);

      // Render the page
      const renderContext = {
        canvasContext: ctx,
        viewport: viewport
      };

      await page.render(renderContext).promise;

    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error);
      // Create a placeholder for failed pages
      canvas.width = 600;
      canvas.height = 800;
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Failed to load page ${pageNum}`, canvas.width / 2, canvas.height / 2);
    }

    return canvas;
  }

  async previousPage(): Promise<void> {
    if (this.currentPage > 1) {
      this.scrollToPage(this.currentPage - 1);
    }
  }

  async nextPage(): Promise<void> {
    if (this.currentPage < this.totalPages) {
      this.scrollToPage(this.currentPage + 1);
    }
  }

  private scrollToPage(pageNum: number, behavior: ScrollBehavior = 'smooth'): void {
    if (pageNum < 1 || pageNum > this.totalPages) return;

    const targetCanvas = this.canvases[pageNum - 1];
    if (!targetCanvas) return;

    if (behavior === 'smooth') {
      targetCanvas.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Direct scrollTop math — avoids scrollIntoView semantics that can interact
    // badly with CSS `scroll-behavior: smooth` or iOS momentum scrolling and
    // leave the container on page 1 during the initial restore.
    const containerRect = this.canvasContainer.getBoundingClientRect();
    const canvasRect = targetCanvas.getBoundingClientRect();
    this.canvasContainer.scrollTop += canvasRect.top - containerRect.top;
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.emitStateChange();
  }

  private emitStateChange(): void {
    const state: PDFViewerState = {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      isLoading: this.isLoading,
      error: this.error || undefined
    };

    this.onStateChange?.(state);
    this.stateChangeCallbacks.forEach(callback => callback(state));
  }

  addStateChangeCallback(callback: (state: PDFViewerState) => void): void {
    this.stateChangeCallbacks.push(callback);
  }

  getState(): PDFViewerState {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      isLoading: this.isLoading,
      error: this.error || undefined
    };
  }

  private reconcileVisiblePage(): void {
    if (this.canvases.length === 0) return;

    const containerRect = this.canvasContainer.getBoundingClientRect();
    let mostVisiblePage = this.currentPage;
    let maxRatio = 0;

    for (const canvas of this.canvases) {
      const rect = canvas.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top));
      const area = rect.width * rect.height;
      if (area <= 0) continue;

      const ratio = (visibleWidth * visibleHeight) / area;
      if (ratio > maxRatio) {
        maxRatio = ratio;
        mostVisiblePage = parseInt(canvas.dataset.pageNum || '1');
      }
    }

    if (mostVisiblePage !== this.currentPage && maxRatio > 0.3) {
      this.currentPage = mostVisiblePage;
      this.emitStateChange();
    }
  }

  close(triggerOnClose: boolean = true): void {
    if (this.isClosed) {
      return;
    }
    this.isClosed = true;

    // Capture the actual visible page before navigation tears down the viewer.
    if (this.hasInitialPageState) {
      this.reconcileVisiblePage();
    }

    // Clean up PDF resources
    this.pdfDoc = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;
    this.error = null;
    this.hasInitialPageState = false;

    // Clear canvases
    this.clearCanvases();

    // Clean up global event listeners to avoid leaks
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    // Trigger the close callback when this is a user-initiated close.
    if (triggerOnClose && this.onClose) {
      this.onClose();
    }
  }

  // Utility methods
  getCurrentPage(): number {
    return this.currentPage;
  }

  getTotalPages(): number {
    return this.totalPages;
  }

  isLoaded(): boolean {
    return this.pdfDoc !== null;
  }
}
